'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import OceanBackground from './OceanBackground';
import DemoCredentials from './DemoCredentials';
import AppLogo from '@/components/ui/AppLogo';

export default function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [role, setRole] = useState<'player' | 'admin'>('player');

  return (
    <div className="min-h-screen flex overflow-hidden" style={{ background: 'var(--background)' }}>
      {/* Left Panel — Ocean Visual */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative flex-col items-center justify-center overflow-hidden ocean-shimmer">
        <OceanBackground />
        <div className="relative z-10 flex flex-col items-center gap-6 px-12 text-center">
          <div className="flex items-center gap-3">
            <AppLogo size={56} />
            <span className="text-4xl font-bold tracking-tight neon-text-cyan">FishBlast</span>
          </div>
          <p className="text-lg text-secondary-foreground max-w-sm leading-relaxed">
            The ultimate multiplayer fish shooting arcade. Join tables, power up your cannon, and hunt legendary boss fish.
          </p>
          <div className="flex gap-6 mt-4">
            {[
              { label: 'Active Tables', value: '24' },
              { label: 'Players Online', value: '1,847' },
              { label: 'Credits Won Today', value: '2.4M' },
            ].map((stat) => (
              <div key={`stat-${stat.label}`} className="flex flex-col items-center gap-1">
                <span className="text-2xl font-bold font-mono-nums neon-text-gold">{stat.value}</span>
                <span className="text-xs text-muted-foreground uppercase tracking-widest">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel — Auth Form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex flex-col items-center justify-center px-6 py-12 min-h-screen">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile Logo */}
          <div className="flex lg:hidden items-center justify-center gap-3 mb-8">
            <AppLogo size={40} />
            <span className="text-2xl font-bold neon-text-cyan">FishBlast</span>
          </div>

          {/* Role Selector */}
          <div className="flex rounded-lg p-1 mb-6" style={{ background: 'var(--muted)' }}>
            {(['player', 'admin'] as const).map((r) => (
              <button
                key={`role-${r}`}
                onClick={() => setRole(r)}
                className={`flex-1 py-2 rounded-md text-sm font-semibold transition-all duration-200 capitalize ${
                  role === r
                    ? 'btn-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                {r === 'player' ? '🎮 Player' : '⚙️ Admin'}
              </button>
            ))}
          </div>

          {/* Mode Tabs */}
          <div className="flex border-b mb-8" style={{ borderColor: 'var(--border)' }}>
            {(['login', 'signup'] as const).map((m) => (
              <button
                key={`mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 pb-3 text-sm font-semibold transition-all duration-200 capitalize ${
                  mode === m
                    ? 'neon-text-cyan border-b-2' :'text-muted-foreground hover:text-foreground'
                }`}
                style={mode === m ? { borderBottomColor: 'var(--primary)' } : {}}
              >
                {m === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Form */}
          {mode === 'login' ? (
            <LoginForm role={role} />
          ) : (
            <SignUpForm role={role} onSwitchToLogin={() => setMode('login')} />
          )}

          {/* Demo Credentials */}
          <DemoCredentials role={role} />
        </div>
      </div>
    </div>
  );
}